import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, 
  Filter, 
  MoreHorizontal,
  Edit,
  Trash2,
  Tag,
  Calendar,
  DollarSign,
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  BarChart3,
  X,
  ChevronDown,
  ChevronRight,
  Check,
  Plus
} from 'lucide-react';
import PieceCard from '../components/PieceCard';
import LoadingScreen from '../components/LoadingScreen';
import PieceActivityAndHolders from '../components/PieceActivityAndHolders';
import VerifiedBadge from '../components/VerifiedBadge';
import ModelViewer from '../components/ModelViewer.jsx';
import { useWallet } from '../contexts/WalletContext';
import { useSolPrice } from '../contexts/SolPriceContext';
import { useFavorites } from '../hooks/useFavorites';
import WalletRequired from '../components/WalletRequired';
import { getUserListings, updatePieceListingStatus, getUserDrafts, getCapsuleForPiece } from '../utils/supabaseDatabase';
import { getMarketplacePiecesByCreator } from '../utils/marketplaceQueries';
import { supabase } from '../lib/supabase';
import { fetchWalletNFTs } from '../utils/marketplaceTransactions';
import { normalizePieces } from '../utils/pieceNormalizer.js';

const Listings = () => {
  const navigate = useNavigate();
  const { isConnected, balance, publicKey, wallet } = useWallet();
  const { solPrice } = useSolPrice(); // Get real-time SOL price from global context
  const { toggleFavorite, isFavorited } = useFavorites();
  const [listings, setListings] = useState([]);
  const [piecesToList, setPiecesToList] = useState([]); // Pieces user owns that can be listed
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('recent');
  const [editPriceModal, setEditPriceModal] = useState({ isOpen: false, item: null, newPrice: '' });
  const [editPriceConfirmModal, setEditPriceConfirmModal] = useState({ isOpen: false, item: null, newPrice: '' });
  const [cancelModal, setCancelModal] = useState({ isOpen: false, item: null });
  const [viewDetailsModal, setViewDetailsModal] = useState({ isOpen: false, item: null });
  const [listForSaleModal, setListForSaleModal] = useState({ isOpen: false, item: null });
  const [activityPanelOpen, setActivityPanelOpen] = useState(false);
  const [listingForm, setListingForm] = useState({ price: '', duration: '30' });
  const [modalViewMode, setModalViewMode] = useState('3d'); // '3d' or 'thumbnail'
  const [threeDStatus, setThreeDStatus] = useState('loading'); // 'loading', 'loaded', 'error'
  const [threeDError, setThreeDError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [clusterSwitchModal, setClusterSwitchModal] = useState({
    isOpen: false,
    targetCluster: null,
    currentCluster: null,
    item: null,
    listingPrice: null,
    isRetrying: false,
  });
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('to-list'); // 'to-list' or 'listed'
  const statusDropdownRef = useRef(null);
  const sortDropdownRef = useRef(null);
  const [pieceCapsuleInfo, setPieceCapsuleInfo] = useState(null);
  
  // Notification system
  const [notification, setNotification] = useState({ show: false, type: 'success', message: '' });

  // Mock listings data - in a real app, this would come from an API
  const mockListings = [
    {
      id: 'listing-1',
      name: 'Neon Cyber Jacket',
      description: 'A futuristic jacket with LED accents and cyberpunk styling',
      category: 'Tops',
      color: 'Neon Green',
      price: '3.5',
      originalPrice: '2.8', // What they originally paid
      status: 'active', // active, sold, expired, cancelled
      views: 450,
      likes: 32,
      offers: 3,
      listedAt: '2024-01-20T10:30:00Z',
      expiresAt: '2024-02-19T10:30:00Z',
      modelFile: {
        dataUrl: 'data:model/gltf-binary;base64,',
        name: 'neon-jacket.glb',
        type: 'model/gltf-binary'
      },
      createdDate: '2024-01-15T08:00:00Z',
      creator: {
        name: 'Alex Chen',
        username: 'alexchen_designs',
        piecesCount: 12
      }
    },
    {
      id: 'listing-2',
      name: 'Digital Sneakers',
      description: 'Virtual sneakers with animated laces',
      category: 'Shoes',
      color: 'White',
      price: '2.1',
      originalPrice: '1.8',
      status: 'active',
      views: 89,
      likes: 12,
      offers: 1,
      listedAt: '2024-01-18T14:20:00Z',
      expiresAt: '2024-02-17T14:20:00Z',
      modelFile: {
        dataUrl: 'data:model/gltf-binary;base64,',
        name: 'digital-sneakers.glb',
        type: 'model/gltf-binary'
      },
      createdDate: '2024-01-10T12:00:00Z',
      creator: {
        name: 'Sarah Kim',
        username: 'sarahkim_3d',
        piecesCount: 8
      }
    },
    {
      id: 'listing-3',
      name: 'Metaverse Cap',
      description: 'A cap that changes color based on your mood',
      category: 'Hats',
      color: 'Rainbow',
      price: '1.5',
      originalPrice: '1.2',
      status: 'sold',
      views: 67,
      likes: 8,
      offers: 0,
      listedAt: '2024-01-15T09:15:00Z',
      soldAt: '2024-01-22T16:45:00Z',
      modelFile: {
        dataUrl: 'data:model/gltf-binary;base64,',
        name: 'metaverse-cap.glb',
        type: 'model/gltf-binary'
      },
      createdDate: '2024-01-05T15:30:00Z',
      creator: {
        name: 'Mike Rodriguez',
        username: 'mike_rodriguez_art',
        piecesCount: 15
      }
    }
  ];

  // Helper function to show notifications
  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: 'success' });
    }, 5000);
  };

  useEffect(() => {
    loadListings();
  }, [publicKey, statusFilter]);

  // Check if piece belongs to a capsule when detail modal opens
  useEffect(() => {
    if (!viewDetailsModal.isOpen || !viewDetailsModal.item?.id) {
      setPieceCapsuleInfo(null);
      return;
    }

    const checkCapsule = async () => {
      try {
        let itemId = viewDetailsModal.item.id;
        const itemIdString = String(itemId);
        
        if (itemIdString.startsWith('supabase_')) {
          itemId = itemIdString.replace('supabase_', '');
          if (!isNaN(itemId)) {
            itemId = parseInt(itemId, 10);
          }
        } else if (itemIdString.startsWith('local_')) {
          console.log('🔍 Local piece detected, skipping capsule check');
          setPieceCapsuleInfo(null);
          return;
        }
        
        console.log('🔍 [LISTINGS] Checking capsule for piece:', itemId);
        const capsuleInfo = await getCapsuleForPiece(itemId);
        console.log('🔍 [LISTINGS] Capsule info result:', capsuleInfo);
        
        setPieceCapsuleInfo(capsuleInfo);
      } catch (error) {
        console.error('❌ [LISTINGS] Error fetching capsule info:', error);
        setPieceCapsuleInfo(null);
      }
    };

    checkCapsule();
  }, [viewDetailsModal.isOpen, viewDetailsModal.item]);

  // Handle click outside dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
        setIsStatusDropdownOpen(false);
      }
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target)) {
        setIsSortDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Sort options
  const sortOptions = [
    { id: 'recent', name: 'Most Recent' },
    { id: 'price-low', name: 'Price: Low to High' },
    { id: 'price-high', name: 'Price: High to Low' },
    { id: 'expiring', name: 'Expiring Soon' }
  ];

  // Status filter options
  const statusOptions = [
    { id: 'all', name: 'All Status' },
    { id: 'active', name: 'Active' },
    { id: 'sold', name: 'Sold' },
    { id: 'expired', name: 'Expired' },
    { id: 'cancelled', name: 'Cancelled' }
  ];

  // Handle dropdown changes
  const handleSortChange = (optionId) => {
    setSortBy(optionId);
    setIsSortDropdownOpen(false);
  };

  const handleStatusChange = (optionId) => {
    setStatusFilter(optionId);
    setIsStatusDropdownOpen(false);
  };

  const loadListings = async () => {
    setLoading(true);
    try {
      if (!publicKey) {
        setListings([]);
        setPiecesToList([]);
        setLoading(false);
        return;
      }

      const currentWallet = publicKey.toString();

      // OPTIMIZATION: Load data in parallel
      const [activeListingsResponse, soldPiecesResponse, currentPiecesResponse] = await Promise.all([
        // Active listings only (for display)
        getUserListings(currentWallet, statusFilter === 'all' ? null : statusFilter, 0, 50),
        // Get sale activities where this user was the seller
        supabase
          .from('nft_activity')
          .select('price, amount_lamports, created_at')
          .eq('from_address', currentWallet)
          .eq('event_type', 'sale')
          .order('created_at', { ascending: false })
          .limit(200),
        // Current pieces owned by this wallet (for "pieces to list")
        supabase
          .from('pieces')
          .select(`
            *,
            social_links,
            creators(
              id,
              wallet,
              handle,
              is_verified,
              verification_type,
              profile_picture_url,
              bio,
              social_links,
              total_pieces_created
            )
          `)
          .eq('holder_wallet', currentWallet)
          .order('created_at', { ascending: false })
          .limit(100)
      ]);

      // Set active listings for display
      const userListings = activeListingsResponse.listings || activeListingsResponse || [];
      console.log('📋 Active listings with creator data:', userListings[0]);
      
      // Process sold pieces from activity data
      let soldPiecesData = [];
      if (!soldPiecesResponse.error && soldPiecesResponse.data) {
        soldPiecesData = soldPiecesResponse.data.map(activity => ({
          status: 'sold',
          listing_status: 'sold',
          price: activity.price || (activity.amount_lamports ? activity.amount_lamports / 1e9 : 0),
          sold_at: activity.created_at
        }));
        console.log('💰 Total sales found:', soldPiecesData.length, 'Total value:', 
          soldPiecesData.reduce((sum, p) => sum + parseFloat(p.price || 0), 0).toFixed(2), 'SOL');
      }

      // Combine active listings with sold pieces for stats calculation
      setListings([...userListings, ...soldPiecesData]);

      // Process current pieces to list
      if (!currentPiecesResponse.error && currentPiecesResponse.data) {
        const allPieces = currentPiecesResponse.data.map(piece => ({
          ...piece,
          id: piece.id,
          name: piece.name || 'Unnamed Piece',
          thumbnailUrl: piece.thumbnail_url || '/images/piece-placeholder.png',
          thumbnail: {
            dataUrl: piece.thumbnail_url || '/images/piece-placeholder.png'
          },
          modelUrl: piece.preview_glb_url || piece.model_url,
          // Use actual purchase price (last_sale_price_lamports) first, then fall back to listing/base price
          price: piece.last_sale_price_lamports 
            ? (piece.last_sale_price_lamports / 1_000_000_000).toString()
            : (piece.listing_price?.toString() || piece.price?.toString() || '0'),
          status: piece.listing_status,
          listing_status: piece.listing_status,
          category: piece.category,
          color: piece.color,
          description: piece.description,
          quantity: piece.quantity || 1,
          creator: {
            name: piece.creators?.handle || 'Unknown',
            username: piece.creators?.handle || 'unknown',
            piecesCount: piece.creators?.total_pieces_created || 0,
            wallet: piece.creators?.wallet || piece.creator_wallet,
            verified: piece.creators?.is_verified || false,
            profilePicture: piece.creators?.profile_picture_url
          },
          creator_wallet: piece.creator_wallet
        }));

        // Get mint addresses of all active listings to exclude them from pieces to list
        const activeListingMints = new Set(userListings.map(l => l.mint_address).filter(Boolean));
        
        // Filter to only pieces that can be listed:
        // - Must be minted
        // - NOT currently listed (listing_status !== 'listed')
        // - NOT in active listings array (double-check to prevent duplicates)
        const unlisted = allPieces.filter(piece => 
          piece.mint_address && // Must be minted
          piece.listing_status !== 'listed' && // Not currently listed
          !activeListingMints.has(piece.mint_address) // Not in active listings (extra safety check)
        );

        console.log('📦 Pieces to list with creator data:', unlisted[0]);
        console.log(`✅ Active listings: ${userListings.length}, Pieces to list: ${unlisted.length}`);
        setPiecesToList(unlisted);
      } else {
        setPiecesToList([]);
      }

    } catch (error) {
      console.error('Error loading data:', error);
      setListings([]);
      setPiecesToList([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort listings
  const getFilteredListings = () => {
    // Start with only ACTIVE listings (exclude sold/expired/cancelled for stats)
    let filtered = listings.filter(l => 
      l.status === 'listed' || l.status === 'active'
    );

    // Filter by search term
    if (searchTerm.trim()) {
      filtered = filtered.filter(listing => 
        listing.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        listing.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
        listing.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by additional status (if not 'all')
    if (statusFilter !== 'all') {
      filtered = filtered.filter(listing => listing.status === statusFilter);
    }

    // Sort listings
    switch (sortBy) {
      case 'recent':
        filtered.sort((a, b) => new Date(b.listedAt) - new Date(a.listedAt));
        break;
      case 'price-low':
        filtered.sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
        break;
      case 'price-high':
        filtered.sort((a, b) => parseFloat(b.price) - parseFloat(a.price));
        break;
      case 'expiring':
        filtered.sort((a, b) => new Date(a.expiresAt) - new Date(b.expiresAt));
        break;
      default:
        break;
    }

    return filtered;
  };

  // Get status icon and color
  const getStatusInfo = (status) => {
    switch (status) {
      case 'listed':
        return { icon: <Clock size={16} />, color: 'text-green-400', bg: 'bg-green-500/10', label: 'Active' };
      case 'sold':
        return { icon: <CheckCircle size={16} />, color: 'text-blue-400', bg: 'bg-blue-500/10', label: 'Sold' };
      case 'expired':
        return { icon: <XCircle size={16} />, color: 'text-gray-400', bg: 'bg-gray-500/10', label: 'Expired' };
      case 'cancelled':
        return { icon: <AlertCircle size={16} />, color: 'text-red-400', bg: 'bg-red-500/10', label: 'Cancelled' };
      case 'draft':
        return { icon: <Clock size={16} />, color: 'text-gray-400', bg: 'bg-gray-500/10', label: 'Draft' };
      default:
        return { icon: <Clock size={16} />, color: 'text-gray-400', bg: 'bg-gray-500/10', label: 'Unknown' };
    }
  };

  // Calculate time remaining
  const getTimeRemaining = (expiresAt) => {
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diff = expiry - now;
    
    if (diff <= 0) return 'Expired';
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) return `${days}d ${hours}h left`;
    return `${hours}h left`;
  };

  // Handle edit price
  const handleEditPrice = (item) => {
    if (item.status === 'sold') {
      showNotification('Cannot edit price of sold items', 'error');
      return;
    }
    setEditPriceModal({ isOpen: true, item, newPrice: item.price });
  };

  const confirmEditPrice = () => {
    const { item, newPrice } = editPriceModal;
    if (!newPrice || parseFloat(newPrice) <= 0) {
      showNotification('Please enter a valid price', 'error');
      return;
    }

    // Show confirmation modal
    setEditPriceConfirmModal({ isOpen: true, item, newPrice });
    setEditPriceModal({ isOpen: false, item: null, newPrice: '' });
  };

  const finalConfirmEditPrice = () => {
    const { item, newPrice } = editPriceConfirmModal;
    
    const updatedListings = listings.map(listing => 
      listing.id === item.id 
        ? { ...listing, price: newPrice }
        : listing
    );
    setListings(updatedListings);
    setEditPriceConfirmModal({ isOpen: false, item: null, newPrice: '' });
    showNotification(`Price updated to ${newPrice} SOL`, 'success');
  };

  // Handle cancel listing
  const handleCancelListing = (item) => {
    if (item.status === 'sold') {
      showNotification('Cannot cancel sold items', 'error');
      return;
    }
    setCancelModal({ isOpen: true, item });
  };

  const confirmCancelListing = async () => {
    const { item } = cancelModal;
    
    if (!isConnected || !publicKey) {
      showNotification('Please connect your wallet to cancel listings', 'error');
      return;
    }
    
    setIsProcessing(true);
    
    try {
      console.log('❌ Canceling listing:', item.name);
      console.log('  - Mint address:', item.mint_address);
      console.log('  - Piece ID:', item.id);
      
      // First, try to get the active listing record for this piece
      // IMPORTANT: Query by mint_address, not piece_id, to handle multi-quantity pieces correctly
      // (multiple pieces can have the same piece_id but different mint_address)
      const { data: listingData, error: listingError } = await supabase
        .from('listings')
        .select('*')
        .eq('mint_address', item.mint_address)
        .eq('status', 'active')
        .maybeSingle();
      
      if (listingData) {
        // NEW SYSTEM: Listing exists in listings table, use proper cancel flow
        console.log('📋 Found listing in listings table:', listingData.id);
        
        // Import the cancel listing function
        const { cancelListing } = await import('../utils/resaleMarketplace');
        
        // Cancel the listing (this calls the edge function)
        const result = await cancelListing(wallet, listingData, item);
        
        if (!result.success) {
          throw new Error(result.message || 'Failed to cancel listing');
        }
        
        console.log('✅ Listing canceled via edge function:', result);
      } else {
        // LEGACY SYSTEM: Old piece that was listed before listings table existed
        // Just update the piece status directly
        console.log('⚠️ No listing found in listings table (legacy piece), updating piece status directly');
        
        const { error: updateError } = await supabase
          .from('pieces')
          .update({
            listing_status: 'draft',
            listing_price: null,
            listed_at: null,
            is_delegate_active: false,
            listing_delegate: null,
            seller_ata: null,
            delegate_expires_at: null,
            updated_at: new Date().toISOString(),
          })
          .eq('id', item.id)
          .eq('holder_wallet', publicKey.toString()); // Safety check: only update if you own it
        
        if (updateError) {
          throw new Error(`Failed to update piece: ${updateError.message}`);
        }
        
        console.log('✅ Legacy listing canceled (piece status updated)');
      }
      
      showNotification(`${item.name} listing cancelled successfully!`, 'success');
      
      // Close modal and reload all data
      setCancelModal({ isOpen: false, item: null });
      await loadListings();
      
    } catch (error) {
      console.error('❌ Cancel failed:', error);
      showNotification(`Failed to cancel listing: ${error.message}`, 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle view details
  const handleViewDetails = (item) => {
    // Normalize the piece data to ensure verificationType is correctly mapped
    const normalizedPiece = item ? normalizePieces([item])[0] : null;
    setViewDetailsModal({ isOpen: true, item: normalizedPiece });
    
    // Check if piece has 3D model and set initial view mode
    const modelBucket = item?.preview_bucket || item?.model_bucket;
    const modelKey = item?.preview_key || item?.model_key;
    const fallbackSrc = normalizedPiece?.previewUrl || normalizedPiece?.modelUrl;
    const has3DModel = (modelBucket && modelKey) || (fallbackSrc && (fallbackSrc.includes('.glb') || fallbackSrc.includes('.gltf')));
    
    if (has3DModel) {
      setModalViewMode('3d');
      setThreeDStatus('loading');
      setThreeDError(null);
    } else {
      setModalViewMode('thumbnail');
      setThreeDStatus('error');
      setThreeDError('No 3D model available');
    }
  };

  const closeViewDetailsModal = () => {
    setViewDetailsModal({ isOpen: false, item: null });
    setActivityPanelOpen(false);
  };

  // Handle account button click - navigate to creator profile
  const handleAccountClick = (item) => {
    const creatorWallet = item?.originalCreator?.wallet || item?.creator?.wallet || item?.creator_wallet;
    if (creatorWallet) {
      navigate(`/creator/${creatorWallet}`);
    }
  };

  // List for Sale functions
  const handleListForSale = (item) => {
    console.log('🔍 Opening list modal for piece:', item.name);
    setListForSaleModal({ isOpen: true, item });
    setListingForm({
      price: item.price || '',
      duration: '30'
    });
  };

  const closeListForSaleModal = () => {
    setListForSaleModal({ isOpen: false, item: null });
    setListingForm({
      price: '',
      duration: '30'
    });
  };

  const processListing = async () => {
    if (!isConnected || !publicKey) {
      showNotification('Please connect your wallet to list items for sale', 'error');
      return;
    }

    const { item } = listForSaleModal;
    const listingPrice = parseFloat(listingForm.price);
    
    if (isNaN(listingPrice) || listingPrice <= 0) {
      showNotification('Please enter a valid listing price', 'error');
      return;
    }

    // Validate item data
    if (!item || !item.id) {
      console.error('❌ Invalid item data:', item);
      showNotification('Unable to list this item. Please refresh the page and try again.', 'error');
      return;
    }

    // Security check: Verify user owns the piece
    const currentWallet = publicKey.toString();
    const isCreator = item.creator_wallet === currentWallet;
    const isHolder = item.holder_wallet === currentWallet;
    
    if (!isCreator && !isHolder) {
      console.error('❌ Unauthorized: User does not own this piece');
      showNotification('You can only list pieces that you own. Please refresh the page and try again.', 'error');
      return;
    }

    setIsProcessing(true);
    
    try {
      console.log('📝 Creating listing:', {
        pieceName: item.name,
        pieceId: item.id,
        listingPrice: listingPrice,
        wallet: currentWallet
      });
      
      // Import the listing function
      const { createListing } = await import('../utils/resaleMarketplace');
      
      const result = await createListing(wallet, item, listingPrice);
      
      console.log('✅ Listing created:', result);
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to create listing');
      }
      
      showNotification(`${item.name} listed for ${listingPrice} SOL! ✓`, 'success');
      closeListForSaleModal();
      await loadListings();
      
    } catch (error) {
      console.error('❌ Listing failed:', error);
      
      // Handle WRONG_CLUSTER error
      if (error.code === 'WRONG_CLUSTER') {
        setClusterSwitchModal({
          isOpen: true,
          targetCluster: error.detectedCluster,
          currentCluster: error.currentCluster,
          item: item,
          listingPrice: listingPrice,
          isRetrying: false,
        });
        setIsProcessing(false);
        return;
      }
      
      // User-friendly error messages
      let errorMessage = 'Could not create listing. Please try again.';
      
      if (error.message?.includes('Already listed') || error.message?.includes('already listed')) {
        errorMessage = 'This item is already listed.';
      } else if (error.message?.includes('Only the current owner') || error.message?.includes('do not own')) {
        errorMessage = 'You can only list items you currently own.';
      } else if (error.code === 'PGRST116') {
        errorMessage = 'Item not found. Please refresh the page.';
      } else if (error.message?.includes('network') || error.message?.includes('timeout')) {
        errorMessage = 'Network error. Please check your connection.';
      } else if (error.message?.includes('not minted')) {
        errorMessage = 'This item must be minted before listing.';
      }
      
      showNotification(errorMessage, 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle cluster switch and retry listing
  const handleClusterSwitch = async () => {
    const { targetCluster, item, listingPrice } = clusterSwitchModal;
    
    try {
      setClusterSwitchModal(prev => ({ ...prev, isRetrying: true }));
      
      const { setRpcCluster } = await import('../config/rpc');
      console.log(`🔄 Switching cluster to ${targetCluster}...`);
      setRpcCluster(targetCluster);
      
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      if (!wallet || !wallet.connected || !publicKey) {
        setClusterSwitchModal(prev => ({ ...prev, isRetrying: false }));
        showNotification('Please reconnect your wallet and try listing again.', 'info');
        closeListForSaleModal();
        setClusterSwitchModal({ isOpen: false, targetCluster: null, currentCluster: null, item: null, listingPrice: null, isRetrying: false });
        return;
      }
      
      console.log('🔄 Retrying listing...');
      const { createListing } = await import('../utils/resaleMarketplace');
      const result = await createListing(wallet, item, listingPrice, { cluster: targetCluster });
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to create listing after cluster switch');
      }
      
      console.log('✅ Listing created successfully after cluster switch!');
      setClusterSwitchModal({ isOpen: false, targetCluster: null, currentCluster: null, item: null, listingPrice: null, isRetrying: false });
      closeListForSaleModal();
      showNotification(`${item.name} listed for ${listingPrice} SOL! ✓`, 'success');
      await loadListings();
      
    } catch (error) {
      console.error('❌ Retry failed:', error);
      setClusterSwitchModal(prev => ({ ...prev, isRetrying: false }));
      
      if (error.code === 'WRONG_CLUSTER') {
        showNotification('Please switch your wallet to the correct network and try again.', 'error');
      } else {
        showNotification('Unable to list item. Please try again.', 'error');
      }
    }
  };

  // Calculate total stats
  const getTotalStats = () => {
    const active = listings.filter(l => l.status === 'listed').length;
    const sold = listings.filter(l => l.status === 'sold').length;
    const totalValue = listings
      .filter(l => l.status === 'sold')
      .reduce((sum, l) => sum + parseFloat(l.price), 0);
    
    return { active, sold, totalValue };
  };

  const stats = getTotalStats();
  const filteredListings = getFilteredListings();

  if (loading) {
    return <LoadingScreen message="Loading listings" />;
  }

  return (
    <WalletRequired 
      message="You need to connect your wallet to view and manage your listings."
      action="manage listings"
    >
      <div className="min-h-screen bg-black">
        <div className="p-6">
        {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">My Listings</h1>
          <p className="text-gray-400">Manage your marketplace listings and track their performance</p>
        </div>
        
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                <Tag size={20} className="text-green-500" />
              </div>
              <div>
                <div className="text-2xl font-bold text-white">{stats.active}</div>
                <div className="text-gray-400 text-sm">Active Listings</div>
          </div>
        </div>
      </div>

          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center">
                <CheckCircle size={20} className="text-blue-500" />
              </div>
              <div>
                <div className="text-2xl font-bold text-white">{stats.sold}</div>
                <div className="text-gray-400 text-sm">Items Sold</div>
              </div>
            </div>
          </div>

          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-purple-500/20 rounded-full flex items-center justify-center">
                <DollarSign size={20} className="text-purple-500" />
              </div>
              <div>
                <div className="text-2xl font-bold text-white">{stats.totalValue.toFixed(2)} SOL</div>
                <div className="text-gray-500 text-xs">≈ ${(stats.totalValue * solPrice).toFixed(2)} USD</div>
                <div className="text-gray-400 text-sm">Total Sales</div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-[#2a2a2a]">
          <button
            onClick={() => setActiveTab('to-list')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'to-list'
                ? 'text-white border-b-2 border-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Pieces to List ({piecesToList.length})
          </button>
          <button
            onClick={() => setActiveTab('listed')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'listed'
                ? 'text-white border-b-2 border-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Active Listings ({listings.filter(l => l.status === 'listed' || l.status === 'active').length})
          </button>
        </div>

        {/* Filters and Search */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
              <div className="relative">
                <input
                  type="text"
                  placeholder={activeTab === 'to-list' ? 'Search pieces...' : 'Search listings...'}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                className="w-64 px-4 py-2 bg-[#0a0a0a] border border-[#2a2a2a] rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-white"
                />
            </div>
            
              {/* Status Filter - only show for listed tab */}
              {activeTab === 'listed' && (
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-300">Status:</span>
                <div className="relative" ref={statusDropdownRef}>
                  <button
                    onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
                    className="flex items-center justify-between bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl text-white text-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent transition-all duration-200 hover:border-[#3a3a3a] cursor-pointer min-w-[140px]"
                  >
                    <span>{statusOptions.find(opt => opt.id === statusFilter)?.name}</span>
                    <ChevronDown 
                      size={16} 
                      className={`text-gray-400 transition-transform duration-200 ${isStatusDropdownOpen ? 'rotate-180' : ''}`} 
                    />
                  </button>
                  
                  {isStatusDropdownOpen && (
                    <div className="absolute top-full left-0 mt-2 w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl shadow-xl z-50 overflow-hidden">
                      {statusOptions.map((option) => (
                        <button
                          key={option.id}
                          onClick={() => handleStatusChange(option.id)}
                          className={`w-full px-4 py-3 text-left text-sm transition-all duration-200 flex items-center justify-between ${
                            option.id === statusFilter
                              ? 'bg-white/10 text-white'
                              : 'text-gray-300 hover:bg-white/5 hover:text-white'
                          }`}
                        >
                          <span>{option.name}</span>
                          {option.id === statusFilter && (
                            <Check size={16} className="text-white" />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              )}

              {/* Sort Options */}
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-300">Sort by:</span>
                <div className="relative" ref={sortDropdownRef}>
                  <button
                    onClick={() => setIsSortDropdownOpen(!isSortDropdownOpen)}
                    className="flex items-center justify-between bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl text-white text-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent transition-all duration-200 hover:border-[#3a3a3a] cursor-pointer min-w-[140px]"
                  >
                    <span>{sortOptions.find(opt => opt.id === sortBy)?.name}</span>
                    <ChevronDown 
                      size={16} 
                      className={`text-gray-400 transition-transform duration-200 ${isSortDropdownOpen ? 'rotate-180' : ''}`} 
                    />
                  </button>
                  
                  {isSortDropdownOpen && (
                    <div className="absolute top-full left-0 mt-2 w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl shadow-xl z-50 overflow-hidden">
                      {sortOptions.map((option) => (
                        <button
                          key={option.id}
                          onClick={() => handleSortChange(option.id)}
                          className={`w-full px-4 py-3 text-left text-sm transition-all duration-200 flex items-center justify-between ${
                            option.id === sortBy
                              ? 'bg-white/10 text-white'
                              : 'text-gray-300 hover:bg-white/5 hover:text-white'
                          }`}
                        >
                          <span>{option.name}</span>
                          {option.id === sortBy && (
                            <Check size={16} className="text-white" />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
          </div>
        </div>

        {/* Content Grid */}
        <div className="space-y-6">
          {activeTab === 'to-list' ? (
            // Show pieces to list
            piecesToList.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 2xl:grid-cols-8 gap-6">
                {piecesToList
                  .filter(piece =>
                    !searchTerm ||
                    piece.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    piece.category?.toLowerCase().includes(searchTerm.toLowerCase())
                  )
                  .map((piece) => (
                    <div 
                      key={piece.id} 
                      onClick={() => handleViewDetails(piece)}
                      className="group relative bg-[#1a1a1a] border border-white/30 rounded-xl overflow-hidden hover:border-white transition-all duration-300 hover:shadow-xl hover:shadow-white/20 cursor-pointer"
                    >
                      {/* 3D Model Preview Area */}
                      <div className="aspect-square bg-[#2a2a2a] flex flex-col items-center justify-center relative group-hover:bg-[#3a3a3a] transition-colors duration-200">
                        <img 
                          src={piece.thumbnailUrl || '/images/piece-placeholder.png'} 
                          alt={piece.name}
                          className="w-full h-full object-cover"
                        />
                      </div>

                      {/* Simplified Piece Info */}
                      <div className="p-3 space-y-2">
                        {/* Title */}
                        <h3 className="text-white font-bold text-sm line-clamp-1 group-hover:text-gray-200 transition-colors">
                          {piece.name}
                        </h3>

                        {/* Price */}
                        <div className="space-y-0.5">
                          <div className="text-green-400 font-semibold text-sm">
                            {piece.price || '0'} SOL
                          </div>
                          <div className="text-gray-500 text-xs">
                            ≈ ${(parseFloat(piece.price || 0) * solPrice).toFixed(2)} USD
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-20 h-20 bg-gradient-to-br from-gray-600 to-gray-700 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <Tag className="w-10 h-10 text-gray-300" />
                </div>
                <h3 className="text-xl font-medium text-gray-400 mb-2">No pieces to list</h3>
                <p className="text-gray-500 mb-6">
                  You don't have any pieces that can be listed right now. Create or buy some pieces first!
                </p>
              </div>
            )
          ) : (
            // Show active listings
            filteredListings.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 2xl:grid-cols-8 gap-6">
              {filteredListings.map((listing) => {
                const statusInfo = getStatusInfo(listing.status);
                return (
                  <div 
                    key={listing.id} 
                    onClick={() => handleViewDetails(listing)}
                    className="group relative bg-[#1a1a1a] border border-white/30 rounded-xl overflow-hidden hover:border-white transition-all duration-300 hover:shadow-xl hover:shadow-white/20 cursor-pointer"
                  >
                    {/* 3D Model Preview Area */}
                    <div className="aspect-square bg-[#2a2a2a] flex flex-col items-center justify-center relative group-hover:bg-[#3a3a3a] transition-colors duration-200">
                      {listing.thumbnail?.dataUrl ? (
                        <img 
                          src={listing.thumbnail.dataUrl} 
                          alt={listing.name}
                          className="w-full h-full object-cover"
                        />
                      ) : listing.modelFile?.dataUrl ? (
                        <img 
                          src="/images/piece-placeholder.png" 
                          alt="No preview available"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <img 
                          src="/images/piece-placeholder.png" 
                          alt="No preview available"
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>

                    {/* Status Badge */}
                    <div className="absolute top-3 left-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${statusInfo.bg} ${statusInfo.color}`}>
                        {statusInfo.icon}
                        {statusInfo.label}
                      </span>
                    </div>


                    {/* Simplified Piece Info */}
                    <div className="p-3 space-y-2">
                      {/* Title */}
                      <h3 className="text-white font-bold text-sm line-clamp-1 group-hover:text-gray-200 transition-colors">
                        {listing.name}
                      </h3>

                      {/* Price */}
                      <div className="space-y-0.5">
                        <div className="text-green-400 font-semibold text-sm">
                          {listing.price} SOL
                        </div>
                        <div className="text-gray-500 text-xs">
                          ≈ ${(parseFloat(listing.price || 0) * solPrice).toFixed(2)} USD
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-20 h-20 bg-gradient-to-br from-gray-600 to-gray-700 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Tag className="w-10 h-10 text-gray-300" />
              </div>
              <h3 className="text-xl font-medium text-gray-400 mb-2">No listings found</h3>
              <p className="text-gray-500 mb-6">
                {searchTerm || statusFilter !== 'all' 
                  ? 'Try adjusting your search or filters' 
                  : 'You haven\'t listed any pieces for sale yet'}
              </p>
          </div>
          )
          )
          }
        </div>
      </div>

      {/* List for Sale Modal */}
      {listForSaleModal.isOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60]">
          <div className="bg-[#111111] border border-[#2a2a2a] rounded-2xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
                <Plus size={24} className="text-green-500" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white">List for Sale</h3>
                <p className="text-gray-400 text-sm">Set your listing price</p>
              </div>
            </div>
            
            <div className="mb-6">
              <div className="bg-[#0a0a0a] rounded-xl p-4 mb-4">
                <h4 className="text-white font-medium mb-2">{listForSaleModal.item?.name}</h4>
                <div className="text-gray-400 text-sm">
                  {listForSaleModal.item?.category || 'Uncategorized'}
                </div>
              </div>

              <div>
                <label className="block text-white font-medium mb-2">Listing Price (SOL)</label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={listingForm.price}
                    onChange={(e) => setListingForm(prev => ({ ...prev, price: e.target.value }))}
                    placeholder="0.00"
                    className="w-full bg-[#1a1a1a] border border-[#3a3a3a] rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-white focus:ring-1 focus:ring-white"
                  />
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">
                    SOL
                  </div>
                </div>
                {listingForm.price && !isNaN(parseFloat(listingForm.price)) && (
                  <div className="mt-1 text-right text-gray-500 text-sm">
                    ≈ ${(parseFloat(listingForm.price) * solPrice).toFixed(2)} USD
                  </div>
                )}
              </div>
            </div>
  
            <div className="flex gap-3">
              <button
                onClick={closeListForSaleModal}
                disabled={isProcessing}
                className="flex-1 px-4 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-colors duration-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={processListing}
                disabled={!listingForm.price || isNaN(parseFloat(listingForm.price)) || parseFloat(listingForm.price) <= 0 || isProcessing}
                className="flex-1 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isProcessing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Listing...
                  </>
                ) : (
                  <>List for Sale</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cluster Switch Modal */}
      {clusterSwitchModal.isOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[70]">
          <div className="bg-[#111111] border border-[#2a2a2a] rounded-2xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-yellow-500/20 rounded-full flex items-center justify-center">
                <AlertCircle size={24} className="text-yellow-500" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white">Wrong Network Detected</h3>
                <p className="text-gray-400 text-sm">Switch to the correct network</p>
              </div>
            </div>
            
            <div className="mb-6">
              <div className="bg-[#0a0a0a] rounded-xl p-4">
                <p className="text-gray-300 text-sm mb-3">
                  This piece exists on <span className="text-white font-medium">{clusterSwitchModal.targetCluster}</span>, 
                  but you're connected to <span className="text-white font-medium">{clusterSwitchModal.currentCluster}</span>.
                </p>
                <p className="text-gray-400 text-xs">
                  Click below to switch networks and retry the listing.
                </p>
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setClusterSwitchModal({ isOpen: false, targetCluster: null, currentCluster: null, item: null, listingPrice: null, isRetrying: false });
                  closeListForSaleModal();
                }}
                disabled={clusterSwitchModal.isRetrying}
                className="flex-1 px-4 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-colors duration-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleClusterSwitch}
                disabled={clusterSwitchModal.isRetrying}
                className="flex-1 px-4 py-3 bg-yellow-600 hover:bg-yellow-700 text-white rounded-xl transition-colors duration-200 font-medium disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {clusterSwitchModal.isRetrying ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Switching...
                  </>
                ) : (
                  <>Switch Network</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Price Modal */}
      {editPriceModal.isOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60]">
          <div className="bg-[#111111] border border-[#2a2a2a] rounded-2xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center">
                <Edit size={24} className="text-blue-500" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white">Edit Price</h3>
                <p className="text-gray-400 text-sm">Update your listing price</p>
              </div>
            </div>
            
            <div className="mb-6">
              <div className="bg-[#0a0a0a] rounded-xl p-4 mb-4">
                <h4 className="text-white font-medium mb-2">{editPriceModal.item?.name}</h4>
                <div className="text-gray-400 text-sm">
                  Current price: <span className="text-green-400 font-medium">{editPriceModal.item?.price} SOL</span>
                </div>
        </div>

              <div>
                <label className="block text-white font-medium mb-2">New Price (SOL)</label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editPriceModal.newPrice}
                    onChange={(e) => setEditPriceModal(prev => ({ ...prev, newPrice: e.target.value }))}
                    placeholder="0.00"
                    className="w-full bg-[#1a1a1a] border border-[#3a3a3a] rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-white focus:ring-1 focus:ring-white"
                  />
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">
                    SOL
                  </div>
                </div>
                {editPriceModal.newPrice && !isNaN(parseFloat(editPriceModal.newPrice)) && (
                  <div className="mt-1 text-right text-gray-500 text-sm">
                    ≈ ${(parseFloat(editPriceModal.newPrice) * solPrice).toFixed(2)} USD
                  </div>
                )}
                </div>
                  </div>
  
            <div className="flex gap-3">
              <button
                onClick={() => setEditPriceModal({ isOpen: false, item: null, newPrice: '' })}
                className="flex-1 px-4 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={confirmEditPrice}
                disabled={!editPriceModal.newPrice || isNaN(parseFloat(editPriceModal.newPrice)) || parseFloat(editPriceModal.newPrice) <= 0}
                className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Update Price
              </button>
            </div>
          </div>
                  </div>
      )}

      {/* Cancel Listing Modal */}
      {cancelModal.isOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60]">
          <div className="bg-[#111111] border border-[#2a2a2a] rounded-2xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
                <Trash2 size={24} className="text-red-500" />
                  </div>
              <div>
                <h3 className="text-xl font-semibold text-white">Cancel Listing</h3>
                <p className="text-gray-400 text-sm">Are you sure you want to cancel this listing?</p>
                  </div>
                  </div>
            
            <div className="mb-6">
              <div className="bg-[#0a0a0a] rounded-xl p-4">
                <h4 className="text-white font-medium mb-2">{cancelModal.item?.name}</h4>
                <div className="text-gray-400 text-sm">
                  Listed for {cancelModal.item?.price} SOL
                </div>
              </div>
              <div className="mt-4 text-gray-300 text-sm">
                This action cannot be undone. The item will be removed from the marketplace.
              </div>
          </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setCancelModal({ isOpen: false, item: null })}
                disabled={isProcessing}
                className="flex-1 px-4 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Keep Listing
              </button>
              <button
                onClick={confirmCancelListing}
                disabled={isProcessing}
                className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? 'Cancelling...' : 'Cancel Listing'}
              </button>
            </div>
          </div>
                  </div>
      )}

      {/* View Details Modal - Explore Page Style */}
      {viewDetailsModal.isOpen && (
        <div 
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={closeViewDetailsModal}
        >
          <div 
            className="bg-[#111111] border border-[#2a2a2a] rounded-2xl max-w-5xl w-full max-h-[90vh] flex flex-col relative"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-1 overflow-hidden rounded-2xl">
              {/* Left side - Thumbnail/3D Model */}
              <div className="w-1/2 p-6 overflow-y-auto">
                {/* 3D Model Toggle Buttons */}
                {(() => {
                  const normalizedPiece = viewDetailsModal.item ? normalizePieces([viewDetailsModal.item])[0] : null;
                  const has3DModel = normalizedPiece?.previewUrl && (normalizedPiece.previewUrl.includes('.glb') || normalizedPiece.previewUrl.includes('.gltf'));
                  
                  if (has3DModel) {
                    return (
                      <div className="flex mb-4 bg-[#111111] rounded-xl p-1">
                        <button
                          onClick={() => setModalViewMode('3d')}
                          className={`flex-1 py-2.5 px-3 rounded-xl text-sm font-medium transition-all duration-200  ${
                            modalViewMode === '3d'
                              ? 'text-white bg-[#2a2a2a] shadow-lg' 
                              : 'text-gray-400 hover:text-white hover:bg-[#0a0a0a]'
                          }`}
                        >
                          3D Model
                        </button>
                        <button
                          onClick={() => setModalViewMode('thumbnail')}
                          className={`flex-1 py-2.5 px-3 rounded-xl text-sm font-medium transition-all duration-200  ${
                            modalViewMode === 'thumbnail'
                              ? 'text-white bg-[#2a2a2a] shadow-lg' 
                              : 'text-gray-400 hover:text-white hover:bg-[#0a0a0a]'
                          }`}
                        >
                          Thumbnail
                        </button>
                      </div>
                    );
                  }
                  return null;
                })()}

                <div className="rounded-xl flex items-center justify-center mb-6 overflow-hidden aspect-square">
                  {(() => {
                    const normalizedPiece = viewDetailsModal.item ? normalizePieces([viewDetailsModal.item])[0] : null;
                    
                    if (modalViewMode === '3d') {
                      // Try to get bucket/key info from the piece data
                      const modelBucket = viewDetailsModal.item?.preview_bucket || viewDetailsModal.item?.model_bucket;
                      const modelKey = viewDetailsModal.item?.preview_key || viewDetailsModal.item?.model_key;
                      
                      // Fallback to legacy URL if bucket/key not available
                      const fallbackSrc = normalizedPiece?.previewUrl || normalizedPiece?.modelUrl;
                      
                      if (modelBucket && modelKey) {
                        const customOrientation = viewDetailsModal.item?.customOrientation || null;
                        
                        return (
                          <ModelViewer
                            modelBucket={modelBucket}
                            modelKey={modelKey}
                            posterUrl={normalizedPiece?.posterUrl || normalizedPiece?.thumbnailUrl || '/images/piece-placeholder.png'}
                            orientation={customOrientation}
                            className="w-full h-full modal"
                            onLoad={() => {
                              console.log('✅ Modal: 3D model loaded successfully');
                              setThreeDStatus('loaded');
                            }}
                            onError={(error) => {
                              console.error('❌ Modal: 3D model error:', error);
                              setThreeDStatus('error');
                              setThreeDError(error.message || 'Failed to load 3D model');
                            }}
                          />
                        );
                      } else if (fallbackSrc) {
                        const customOrientation = viewDetailsModal.item?.customOrientation || null;
                        
                        return (
                          <ModelViewer
                            src={fallbackSrc}
                            posterUrl={normalizedPiece?.posterUrl || normalizedPiece?.thumbnailUrl || '/images/piece-placeholder.png'}
                            orientation={customOrientation}
                            className="w-full h-full modal"
                            onLoad={() => {
                              console.log('✅ Modal: 3D model loaded successfully');
                              setThreeDStatus('loaded');
                            }}
                            onError={(error) => {
                              console.error('❌ Modal: 3D model error:', error);
                              setThreeDStatus('error');
                              setThreeDError(error.message || 'Failed to load 3D model');
                            }}
                          />
                        );
                      } else {
                        return (
                          <div className="w-full h-full flex items-center justify-center bg-gray-800 rounded-xl">
                            <div className="text-center text-white p-6">
                              <div className="text-6xl mb-4">⚠️</div>
                              <div className="text-xl font-semibold mb-2">No 3D model available</div>
                              <div className="text-sm text-gray-400">This piece doesn't have a 3D model</div>
                            </div>
                          </div>
                        );
                      }
                    } else {
                      // Show thumbnail image
                      return viewDetailsModal.item?.thumbnailUrl ? (
                        <img 
                          src={viewDetailsModal.item.thumbnailUrl} 
                          alt={viewDetailsModal.item.name}
                          className="w-full h-full object-cover rounded-xl"
                          onError={(e) => {
                            console.log('Modal thumbnail load error for:', viewDetailsModal.item.name);
                            e.target.src = '/images/piece-placeholder.png';
                          }}
                        />
                      ) : viewDetailsModal.item?.thumbnail?.dataUrl ? (
                        <img 
                          src={viewDetailsModal.item.thumbnail.dataUrl} 
                          alt={viewDetailsModal.item.name}
                          className="w-full h-full object-cover rounded-xl"
                        />
                      ) : (
                        <img 
                          src="/images/piece-placeholder.png" 
                          alt="No preview available"
                          className="w-full h-full object-cover rounded-xl"
                        />
                      );
                    }
                  })()}
                </div>

                {/* Creator Section */}
                <div className="space-y-4">
                  <div>
                    <div className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-2 px-1">
                      Original Creator
                    </div>
                    <button 
                      onClick={() => handleAccountClick(viewDetailsModal.item)}
                      className="w-full flex items-center gap-3 p-3 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-xl transition-colors group border border-[#1a1a1a]"
                    >
                      <div className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0">
                        {viewDetailsModal.item?.creator?.profilePicture ? (
                          <img
                            src={viewDetailsModal.item.creator.profilePicture}
                            alt={viewDetailsModal.item?.creator?.username || 'Creator'}
                            className="w-full h-full object-cover rounded-full"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-green-500 to-blue-500 rounded-full flex items-center justify-center">
                            <span className="text-white font-bold text-sm">
                              {(viewDetailsModal.item?.creator?.username || 'Creator').charAt(0).toUpperCase()}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 text-left min-w-0">
                        <div className="flex items-center gap-1.5">
                          <div className="text-white font-medium text-sm group-hover:text-gray-200 transition-colors truncate">
                            {viewDetailsModal.item?.creator?.username || 'Original Creator'}
                          </div>
                          {viewDetailsModal.item?.creator?.verified && (
                            <VerifiedBadge 
                              size="sm" 
                              verificationType={viewDetailsModal.item?.creator?.verificationType || 'sales_milestone'}
                            />
                          )}
                        </div>
                        <div className="text-gray-400 text-xs mt-0.5">
                          {viewDetailsModal.item?.creator?.piecesCount || '0'} pieces created
                        </div>
                      </div>
                      <div className="text-gray-400 group-hover:text-white transition-colors flex-shrink-0">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M9 18l6-6-6-6"/>
                        </svg>
                      </div>
                    </button>

                    {/* Creator Royalty */}
                    {viewDetailsModal.item?.royalty_bps !== undefined && viewDetailsModal.item?.royalty_bps !== null && (
                      <div className="mt-3 px-1">
                        <span className="inline-block px-2 py-0.5 bg-green-500/10 border border-green-500/30 rounded-md text-green-400 text-xs">
                          {(viewDetailsModal.item.royalty_bps / 100).toFixed(1)}% Creator Royalty
                        </span>
                      </div>
                    )}

                    {/* Social Links */}
                    {(() => {
                      // Prioritize capsule social links if piece belongs to a capsule, otherwise use piece social links
                      const socialLinks = pieceCapsuleInfo 
                        ? {
                            website: pieceCapsuleInfo.website,
                            twitter: pieceCapsuleInfo.twitter,
                            instagram: pieceCapsuleInfo.instagram,
                            discord: pieceCapsuleInfo.discord
                          }
                        : (viewDetailsModal.item?.social_links || {});
                      const hasSocialLinks = socialLinks.website || socialLinks.twitter || socialLinks.instagram || socialLinks.discord;
                      
                      // DEBUG: Log social links data
                      console.log('🔍 [LISTINGS] Social Links Debug:', {
                        pieceId: viewDetailsModal.item?.id,
                        pieceName: viewDetailsModal.item?.name,
                        pieceCapsuleInfo: pieceCapsuleInfo,
                        usingCapsuleLinks: !!pieceCapsuleInfo,
                        social_links: socialLinks,
                        hasSocialLinks
                      });
                      
                      if (!hasSocialLinks) return null;
                      
                      return (
                        <div className="flex items-center gap-2 mt-3 px-1">
                          {socialLinks.website && (
                            <a
                              href={socialLinks.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors border border-[#1a1a1a] hover:border-[#2a2a2a]"
                              title="Website"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-400 hover:text-white transition-colors">
                                <circle cx="12" cy="12" r="10"/>
                                <line x1="2" y1="12" x2="22" y2="12"/>
                                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                              </svg>
                            </a>
                          )}
                          {socialLinks.twitter && (
                            <a
                              href={socialLinks.twitter}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors border border-[#1a1a1a] hover:border-[#2a2a2a]"
                              title="X (Twitter)"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-gray-400 hover:text-white transition-colors">
                                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                              </svg>
                            </a>
                          )}
                          {socialLinks.instagram && (
                            <a
                              href={socialLinks.instagram}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors border border-[#1a1a1a] hover:border-[#2a2a2a]"
                              title="Instagram"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-400 hover:text-white transition-colors">
                                <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
                                <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
                              </svg>
                            </a>
                          )}
                          {socialLinks.discord && (
                            <a
                              href={socialLinks.discord}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors border border-[#1a1a1a] hover:border-[#2a2a2a]"
                              title="Discord"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-gray-400 hover:text-white transition-colors">
                                <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
                              </svg>
                            </a>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Right side - Details */}
              <div className="w-1/2 p-6 border-l border-[#2a2a2a] overflow-y-auto">
                <div className="flex justify-between items-start mb-6">
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-white mb-2">
                      {viewDetailsModal.item?.name}
                    </h2>
                    <div className="flex items-center gap-2 flex-wrap">
                      {viewDetailsModal.item?.tags && viewDetailsModal.item.tags.length > 0 ? (
                        viewDetailsModal.item.tags.map((tag, index) => (
                          <span 
                            key={index}
                            className="bg-gray-600 text-gray-200 px-3 py-1 rounded-full text-sm font-medium"
                          >
                            {tag}
                          </span>
                        ))
                      ) : (
                        <span className="text-gray-400 text-sm">No tags</span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => setActivityPanelOpen(!activityPanelOpen)}
                    className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                      activityPanelOpen 
                        ? 'bg-[#2a2a2a] text-white hover:bg-[#3a3a3a] border border-[#3a3a3a]' 
                        : 'bg-[#1a1a1a] text-gray-300 hover:bg-[#2a2a2a] hover:text-white border border-[#2a2a2a]'
                    }`}
                  >
                    Activity
                  </button>
                </div>

                <div className="space-y-5">
                  {/* Category and Color */}
                  <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                        <span className="text-gray-300 text-sm font-medium">
                          {viewDetailsModal.item?.category || 'Uncategorized'}
                        </span>
                      </div>
                      {viewDetailsModal.item?.color && (
                        <div className="text-gray-300 text-sm">
                          <span className="text-gray-500">Color: </span>
                          <span className="capitalize font-medium">{viewDetailsModal.item.color}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                    <h3 className="text-white font-semibold mb-2 text-sm">Description</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">
                      {viewDetailsModal.item?.description || 'A premium digital fashion piece with modern styling and high-quality design.'}
                    </p>
                  </div>

                  {/* Price */}
                  <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                    <h3 className="text-white font-semibold mb-3 text-sm">Pricing</h3>
                    <div className="space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400 text-sm">Price</span>
                        <div className="text-green-400 font-bold text-xl">
                          {viewDetailsModal.item?.price} SOL
                        </div>
                      </div>
                      <div className="text-right text-gray-500 text-sm">
                        ≈ ${((viewDetailsModal.item?.price || 0) * solPrice).toFixed(2)} USD
                      </div>
                    </div>
                  </div>

                  {/* Scarcity */}
                  <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                    <div className="text-gray-400 text-sm mb-1">Scarcity</div>
                    <div className="text-white font-semibold text-lg">
                      {viewDetailsModal.item?.quantity || 1}
                    </div>
                  </div>

                  {/* Dropped Date */}
                  <div className="text-gray-500 text-sm pt-2">
                    Dropped {viewDetailsModal.item?.listedAt ? new Date(viewDetailsModal.item.listedAt).toLocaleDateString('en-US', { 
                      year: '2-digit', 
                      month: '2-digit', 
                      day: '2-digit' 
                    }) : 'Recently'}
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-3 pt-4 pb-2">
                    {(() => {
                      // Check if current user is the owner
                      const isOwner = isConnected && publicKey && 
                        viewDetailsModal.item?.holder_wallet === publicKey.toString();
                      
                      // Check if piece is in the active listings (meaning it's actually listed)
                      const isInActiveListings = listings.some(listing => 
                        listing.mint_address === viewDetailsModal.item?.mint_address &&
                        (listing.status === 'listed' || listing.status === 'active')
                      );
                      
                      // Only show cancel listing if:
                      // 1. Piece is actually in active listings, OR
                      // 2. Piece has listing_status 'listed' AND we're viewing from the Active Listings tab
                      const shouldShowCancelListing = isInActiveListings || 
                        (activeTab === 'listed' && 
                         (viewDetailsModal.item?.status === 'listed' || viewDetailsModal.item?.listing_status === 'listed'));
                      
                      if (shouldShowCancelListing) {
                        return (
                          <>
                            <button 
                              onClick={() => handleEditPrice(viewDetailsModal.item)}
                              className="w-full px-4 py-3.5 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-all duration-200 font-semibold text-sm border border-[#3a3a3a]"
                            >
                              Edit Price
                            </button>
                            <button 
                              onClick={() => handleCancelListing(viewDetailsModal.item)}
                              className="w-full px-4 py-3.5 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-all duration-200 font-semibold text-sm shadow-lg hover:shadow-red-500/25"
                            >
                              Cancel Listing
                            </button>
                          </>
                        );
                      } else {
                        // Show list for sale button for pieces you own that aren't listed
                        return (
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              closeViewDetailsModal();
                              handleListForSale(viewDetailsModal.item);
                            }}
                            className="w-full px-4 py-3.5 bg-green-600 hover:bg-green-700 text-white rounded-xl transition-all duration-200 font-semibold text-sm shadow-lg hover:shadow-green-500/25"
                          >
                            List for Sale
                          </button>
                        );
                      }
                    })()}
                  </div>
                </div>
              </div>
            </div>

            {/* Activity Side Panel - slides out from behind modal on desktop, overlay on mobile */}
            <div 
              className="absolute top-0 left-0 md:left-full h-full w-full md:w-80 bg-[#0a0a0a] border-l border-[#2a2a2a] md:rounded-r-2xl transition-all duration-300 ease-in-out shadow-2xl"
              style={{ 
                transform: activityPanelOpen ? 'translateX(0)' : (window.innerWidth < 768 ? 'translateX(-100%)' : 'translateX(-100%)'),
                zIndex: activityPanelOpen ? 50 : 0,
                opacity: activityPanelOpen ? 1 : 0,
                visibility: activityPanelOpen ? 'visible' : 'hidden',
                pointerEvents: activityPanelOpen ? 'auto' : 'none'
              }}
            >
              <div className="h-full flex flex-col p-5">
                <div className="flex items-center justify-between mb-4 pb-3 border-b border-[#2a2a2a]">
                  <h3 className="text-base font-bold text-white">Activity</h3>
                  <button
                    onClick={() => setActivityPanelOpen(false)}
                    className="p-1.5 hover:bg-[#1a1a1a] rounded-lg transition-colors"
                    title="Close Activity Panel"
                  >
                    <ChevronRight size={18} className="text-gray-400" />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto">
                  <PieceActivityAndHolders
                    pieceId={viewDetailsModal.item?.id}
                    pieceName={viewDetailsModal.item?.name}
                    basePrice={parseFloat(viewDetailsModal.item?.price) || 0}
                    solPrice={solPrice || 150}
                    totalQuantity={viewDetailsModal.item?.total_quantity || viewDetailsModal.item?.quantity || 1}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Price Confirmation Modal */}
      {editPriceConfirmModal.isOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[70]">
          <div className="bg-[#111111] border border-[#2a2a2a] rounded-2xl p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center">
                  <Edit size={24} className="text-blue-500" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white">Edit Price?</h3>
                  <p className="text-gray-400 text-sm">Are you sure you want to change the price?</p>
                </div>
              </div>
              <button
                onClick={() => setEditPriceConfirmModal({ isOpen: false, item: null, newPrice: '' })}
                className="p-2 hover:bg-[#2a2a2a] rounded-lg transition-colors"
              >
                <X size={24} className="text-gray-400" />
              </button>
            </div>
            
            <div className="mb-6">
              <div className="bg-[#0a0a0a] rounded-xl p-4">
                <h4 className="text-white font-medium mb-2">{editPriceConfirmModal.item?.name}</h4>
                <div className="text-gray-400 text-sm">
                  Current Price: {editPriceConfirmModal.item?.price} SOL
                </div>
                <div className="text-gray-400 text-sm">
                  New Price: {editPriceConfirmModal.newPrice} SOL
                </div>
              </div>
              <div className="mt-4 text-gray-300 text-sm">
                This will update the listing price and notify potential buyers of the change.
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setEditPriceConfirmModal({ isOpen: false, item: null, newPrice: '' })}
                className="flex-1 px-4 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={finalConfirmEditPrice}
                className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-colors duration-200 font-medium"
              >
                Update Price
            </button>
          </div>
        </div>
      </div>
      )}

      {/* Notification Toast */}
      {notification.show && (
        <div className="fixed top-4 right-4 z-[70] animate-slide-down">
          <div className={`${
            notification.type === 'success' ? 'bg-green-500/20 border-green-500/50' :
            notification.type === 'error' ? 'bg-red-500/20 border-red-500/50' :
            'bg-blue-500/20 border-blue-500/50'
          } border rounded-xl p-4 shadow-xl max-w-md`}>
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                notification.type === 'success' ? 'bg-green-500/20' :
                notification.type === 'error' ? 'bg-red-500/20' :
                'bg-blue-500/20'
              }`}>
                {notification.type === 'success' ? (
                  <CheckCircle size={20} className="text-green-400" />
                ) : notification.type === 'error' ? (
                  <XCircle size={20} className="text-red-400" />
                ) : (
                  <AlertCircle size={20} className="text-blue-400" />
                )}
              </div>
              <div className="flex-1">
                <p className="text-white text-sm">{notification.message}</p>
              </div>
              <button
                onClick={() => setNotification({ ...notification, show: false })}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X size={18} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </WalletRequired>
  );
};

export default Listings; 